import _ from "lodash";

/** External */
import React from "react";
import { connect } from "react-redux";

/** Internal */
import { useI18n } from "Hooks";
import { prepareLoadMoreFeed } from "Utils";
import { compose, graphql } from "lib/apolloClient";
import withLoader from "UIComponents/GqlWithLoader";
import UserNameAvatar from "AppComponents/UserNameAvatar";
import { getFormattedUserName } from "Utils/GetFormattedUserName";
import { NoSearchResultsIllustration } from "@toddle-edu/ds-theme";
import { SearchInput, RadioButton, EmptyState } from "@toddle-edu/ds-web";

/* Relative **/
import classes from "./StudentContentV2.scss";
import { getFilterObject } from "services/filterList";
import { getCourseStudentListWithInviteCodesQuery } from "StudentPortfolio/modules/StudentPortfolioQuery";

const avatarStyles = { height: "32px", randomizeColor: true, shade: "light" };

const StudentContentV2 = props => {
  const {
    students,
    handleSelectionChange,
    searchText,
    setSearchText,
    selectedStudent,
    allChildren,
  } = props;

  const { t } = useI18n(["common"]);

  const formattedStudents = _.map(_.get(students, "edges", []), edge => {
    return {
      ...edge.node,
      name: getFormattedUserName({ user: edge.node, type: "student" }),
    };
  });

  const filteredStudentsBySearchText = _.filter(
    formattedStudents,
    student =>
      searchText === "" ||
      _.includes(_.lowerCase(student.name), _.lowerCase(searchText))
  );

  const childIds = _.map(allChildren, child => child.id);

  const nonChildrenStudents = searchText
    ? _.filter(
        filteredStudentsBySearchText,
        student => !_.includes(childIds, student.id)
      )
    : _.filter(formattedStudents, student => !_.includes(childIds, student.id));

  const updateSearchText = value => {
    setSearchText(value);
  };

  return (
    <div className={classes.container}>
      <div className={`text-body ${classes.subtleText}`}>
        {t("common:select_your_child_from_list")}
      </div>
      <SearchInput
        dsVersion="2.0"
        autoFocus
        size="medium"
        value={searchText}
        onChange={updateSearchText}
        placeholder={t("common:search")}
        focusSearchBySlash
      />
      {_.size(nonChildrenStudents) === 0 ? (
        <EmptyState
          dsVersion="2.0"
          size="small"
          illustration={NoSearchResultsIllustration}
          title={t("common:no_result_found")}
          subtitle={t("common:empty_user_search", {
            label: searchText,
          })}
        />
      ) : (
        <div className={classes.studentList}>
          {_.map(nonChildrenStudents, student => {
            const {
              firstName,
              lastName,
              middleName,
              preferredName,
              prefix,
              suffix,
              pronouns,
              type,
            } = student;
            const userName = getFormattedUserName({
              user: {
                firstName,
                middleName,
                lastName,
                preferredName,
                suffix,
                prefix,
                pronouns,
                type,
              },
            });

            return (
              <div
                key={student.id}
                className={classes.studentContainer}
                onClick={() => handleSelectionChange(student)}
              >
                <div className={classes.studentDetailContainer}>
                  <UserNameAvatar
                    dsVersion="2.0"
                    id={student.id}
                    name={userName}
                    shouldDynamicColor={true}
                    showTooltip={false}
                    showAvatar={true}
                    avatarStyles={avatarStyles}
                  />
                </div>
                <RadioButton
                  dsVersion="2.0"
                  checked={selectedStudent?.id === student.id}
                ></RadioButton>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state, ownProps) => {
  return {
    searchText: ownProps.searchText,
    courseId: ownProps.courseId,
    isData: true,
    isLoading: false,
  };
};

export default compose(
  connect(mapStateToProps, null),
  graphql(getCourseStudentListWithInviteCodesQuery, {
    name: "getCourseStudentPortfolio",
    skip: ({ courseId }) => !courseId,
    options: ({ searchText, courseId }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        id: courseId,
        filters: getFilterObject({
          searchText,
          archivalState: "ACTIVE",
          fetchAcademicYearFromCourse: true,
        }),
      },
    }),
    props({
      getCourseStudentPortfolio,
      ownProps: { isData, isLoading, courseId, searchText },
    }) {
      return {
        getCourseStudentPortfolio,
        students: _.get(getCourseStudentPortfolio, "node.students", []),
        isData: !_.isEmpty(getCourseStudentPortfolio.node) && isData,
        networkStatus: getCourseStudentPortfolio.networkStatus,
        organizationName: _.get(
          getCourseStudentPortfolio,
          "node.organization.name"
        ),
        isLoading:
          _.includes([1, 2], getCourseStudentPortfolio["networkStatus"]) ||
          isLoading,
        loadMoreStudents: prepareLoadMoreFeed({
          queryData: getCourseStudentPortfolio,
          variables: {
            id: courseId,
            filters: getFilterObject({ searchText, archivalState: "ACTIVE" }),
            after: _.get(
              getCourseStudentPortfolio,
              "node.students.pageInfo.endCursor",
              ""
            ),
            key: "previousResult",
          },
        }),
      };
    },
  }),
  withLoader
)(StudentContentV2);
